// Home screen — entry points for daily/as-needed/weekly captures plus the
// journal entry. Kept deliberately simple per the brief — the calendar /
// history view supersedes a "recent days" strip later.

import { dailyProfiles, asNeededProfiles, weeklyProfiles } from './profiles.js';
import { listPhotos, listEntryDates, loadJournal, today } from '../storage/storage.js';
import { esc, formatDate } from './util.js';

export function renderHome(app) {
  app.innerHTML = `
    <header>
      <h1>MaxHealth</h1>
      <p class="subtitle">${esc(formatDate(today()))}</p>
    </header>
    <main>
      <section class="card">
        <div class="card-head">
          <h2>Daily captures</h2>
          <span id="daily-progress" class="progress-chip muted">…</span>
        </div>
        <div id="daily-list" class="capture-list">
          <p class="muted">Loading…</p>
        </div>
      </section>

      <section class="card">
        <h2>As needed</h2>
        <div id="asneeded-list" class="capture-list"></div>
      </section>

      <section class="card">
        <h2>This week</h2>
        <div id="weekly-list" class="capture-list"></div>
      </section>

      <section class="card">
        <h2>Journal</h2>
        <a class="capture-row" href="#/journal">
          <div class="capture-row__label">
            <div class="capture-row__title">Today's journal</div>
            <div class="capture-row__id muted" id="journal-status">…</div>
          </div>
          <div class="capture-row__status">open →</div>
        </a>
      </section>
    </main>
    <footer>
      <a href="#/settings" class="link-btn">Settings</a>
      <span class="muted">v0.3.0</span>
    </footer>
  `;

  renderAll();
}

async function renderAll() {
  const date = today();
  let todaysPhotos = [];
  try { todaysPhotos = await listPhotos(date); } catch (err) { console.warn('listPhotos:', err); }
  const capturedToday = new Set(todaysPhotos.map((p) => p.profile));

  renderDaily(capturedToday);
  renderAsNeeded(todaysPhotos);
  await renderWeekly(date);
  await renderJournalStatus(date);
}

function renderDaily(captured) {
  const host = document.getElementById('daily-list');
  const progress = document.getElementById('daily-progress');
  if (!host) return;

  const profiles = dailyProfiles();
  const done = profiles.filter((p) => captured.has(p.id)).length;
  if (progress) progress.textContent = `${done} / ${profiles.length} done`;

  host.innerHTML = profiles.map((p) => row(p, captured.has(p.id))).join('');
}

function renderAsNeeded(todaysPhotos) {
  const host = document.getElementById('asneeded-list');
  if (!host) return;
  const rashCount = todaysPhotos.filter((p) => p.profile.startsWith('rash')).length;
  const eyeCount  = todaysPhotos.filter((p) => p.profile === 'eye-closeup').length;

  host.innerHTML = `
    <a class="capture-row" href="#/capture/eye-closeup">
      <div class="capture-row__label">
        <div class="capture-row__title">Eye close-up</div>
        <div class="capture-row__id muted">eye-closeup${eyeCount ? ` · ${eyeCount} today` : ''}</div>
      </div>
      <div class="capture-row__status">capture →</div>
    </a>
    <a class="capture-row" href="#/capture/rash">
      <div class="capture-row__label">
        <div class="capture-row__title">+ Add rash photo</div>
        <div class="capture-row__id muted">rash · site picked at capture${rashCount ? ` · ${rashCount} today` : ''}</div>
      </div>
      <div class="capture-row__status">capture →</div>
    </a>
  `;
}

async function renderWeekly(date) {
  const host = document.getElementById('weekly-list');
  if (!host) return;
  const from = isoDaysAgo(date, 6); // last 7 days inclusive
  let recentDates = [];
  try { recentDates = await listEntryDates({ from, to: date }); } catch {}

  // Aggregate profile counts across the week.
  const counts = { 'body-front': 0, 'body-back': 0 };
  for (const d of recentDates) {
    try {
      const photos = await listPhotos(d);
      for (const p of photos) if (counts[p.profile] != null) counts[p.profile]++;
    } catch {}
  }

  const profiles = weeklyProfiles();
  host.innerHTML = profiles.map((p) => {
    const c = counts[p.id] ?? 0;
    const meta = c ? `${p.id} · ${c}× this week` : `${p.id} · not yet this week`;
    return `
      <a class="capture-row ${c ? 'capture-row--done' : ''}" href="#/capture/${encodeURIComponent(p.id)}">
        <div class="capture-row__label">
          <div class="capture-row__title">${esc(p.label)}</div>
          <div class="capture-row__id muted">${esc(meta)}</div>
        </div>
        <div class="capture-row__status">capture →</div>
      </a>
    `;
  }).join('');
}

async function renderJournalStatus(date) {
  const el = document.getElementById('journal-status');
  if (!el) return;
  try {
    const j = await loadJournal(date);
    el.textContent = j ? 'started — open to edit' : 'not yet entered';
  } catch {
    el.textContent = 'open to start';
  }
}

function row(profile, done) {
  return `
    <a class="capture-row ${done ? 'capture-row--done' : ''}" href="#/capture/${encodeURIComponent(profile.id)}">
      <div class="capture-row__label">
        <div class="capture-row__title">${esc(profile.label)}</div>
        <div class="capture-row__id muted">${esc(profile.id)}</div>
      </div>
      <div class="capture-row__status">${done ? '✓ captured' : 'capture →'}</div>
    </a>
  `;
}

function isoDaysAgo(date, n) {
  const [y, m, d] = date.split('-').map(Number);
  const dt = new Date(y, m - 1, d - n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}
